import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Container,
  Dashboard,
  FilterButton,
  PartyRanking,
  Podium,
  Points,
  PointsCell,
  Position,
  RankingTable,
  RankingTableUsers,
  TableBody,
  TableHeader,
  Title,
  TopUsers,
  UserCell,
  UserInfo,
  WinnerBadge,
} from './styles';
import {
  ChartIcon,
  FirstPodiumIcon,
  SecondPodiumIcon,
  ThirdPodiumIcon,
} from '../../assets/svg/SVGComponents';
import api from '../../services/api';
import { IRankingUsersProps } from '../../dtos/IRankingUsersProps';
import IRequestError from '../../dtos/IRequestError';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import LoadingState from './LoadingState';

type FilterType = 'weekly' | 'monthly' | 'ytd';

const RankingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<FilterType>('ytd');
  const [rankingUsers, setRankingUsers] = useState<IRankingUsersProps>();
  const { user, isSessionExpired } = useAuth();
  const { addToast } = useToast();

  const getRankingUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `/ranking/filtered/${activeFilter}?limit=100`,
      );
      setRankingUsers(response.data);
    } catch (err) {
      if (!(await isSessionExpired(err as IRequestError))) {
        addToast({
          type: 'error',
          expression: 'Ops.',
          description: t('toasts.errors.load_data_fail'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeFilter, addToast, isSessionExpired, t]);

  useEffect(() => {
    getRankingUsers();
  }, [getRankingUsers]);

  const topRankedUsers = useMemo(() => {
    const users = rankingUsers?.ranking || [];
    const sortedUsers = [...users].sort((a, b) => a.rank - b.rank);

    const loggedUser = sortedUsers.find(u => u.id === user.id);

    const firstplace = sortedUsers.find(u => u.rank === 1) || sortedUsers[0];
    const secondplace = sortedUsers.find(u => u.rank === 2) || sortedUsers[1];
    const thirdplace = sortedUsers.find(u => u.rank === 3) || sortedUsers[2];

    const hasPodium = !!(firstplace && secondplace && thirdplace);

    // Remove o usuário logado da lista de outros usuários
    const otherUsers = sortedUsers.filter(
      u => u.rank > 3 && u.rank <= 50 && u.id !== user.id,
    );

    // Se o usuário logado não está no pódio e está no ranking, destaca ele
    const userInList =
      loggedUser && loggedUser.rank > 3 && loggedUser.rank <= 50
        ? loggedUser
        : null;

    return {
      firstplace,
      secondplace,
      thirdplace,
      hasPodium,
      otherUsers,
      userInList,
      loggedUser,
    };
  }, [rankingUsers, user.id]);

  return (
    <Container>
      <Title>
        <ChartIcon
          fill="none"
          strokeWidth={2}
          stroke="#ff9800"
          width="2.2rem"
          height="2.2rem"
        />
        <h1>Ranking</h1>
      </Title>
      {isLoading && <LoadingState />}
      {rankingUsers && !isLoading && (
        <Dashboard>
          <FilterButton>
            <li className={activeFilter === 'weekly' ? 'active' : ''}>
              <button type="button" onClick={() => setActiveFilter('weekly')}>
                Semanal
              </button>
            </li>
            <li className={activeFilter === 'monthly' ? 'active' : ''}>
              <button type="button" onClick={() => setActiveFilter('monthly')}>
                Mensal
              </button>
            </li>
            <li className={activeFilter === 'ytd' ? 'active' : ''}>
              <button type="button" onClick={() => setActiveFilter('ytd')}>
                YTD
              </button>
            </li>
          </FilterButton>

          <TopUsers>
            {topRankedUsers.hasPodium && (
              <>
                <PartyRanking>
                  <Avatar
                    $color="#CD7F32"
                    $isLoggedUser={topRankedUsers.thirdplace!.id === user.id}
                  >
                    {topRankedUsers.thirdplace!.id === user.id && (
                      <WinnerBadge>3</WinnerBadge>
                    )}
                    <img
                      src={
                        topRankedUsers.thirdplace!.avatar_url ||
                        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                      }
                      alt={`${topRankedUsers.thirdplace!.name} Avatar`}
                    />
                    <UserInfo>
                      <h2
                        style={
                          topRankedUsers.thirdplace!.id === user.id
                            ? { color: '#2196f3' }
                            : {}
                        }
                      >
                        3º {topRankedUsers.thirdplace!.name}
                      </h2>
                      <p>{topRankedUsers.thirdplace!.school_name}</p>
                    </UserInfo>
                  </Avatar>
                  <Podium>
                    <Points>
                      {topRankedUsers.thirdplace!.total_points} pts
                    </Points>
                    <ThirdPodiumIcon width="15rem" />
                  </Podium>
                </PartyRanking>

                <PartyRanking>
                  <Avatar
                    $color="#FFD700"
                    $isLoggedUser={topRankedUsers.firstplace!.id === user.id}
                    $fisrtPlace={topRankedUsers.firstplace!.id === user.id}
                  >
                    {topRankedUsers.firstplace!.id === user.id && (
                      <WinnerBadge>1</WinnerBadge>
                    )}
                    <img
                      src={
                        topRankedUsers.firstplace!.avatar_url ||
                        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                      }
                      alt={`${topRankedUsers.firstplace!.name} Avatar`}
                    />
                    <UserInfo>
                      <h2
                        style={
                          topRankedUsers.firstplace!.id === user.id
                            ? { color: '#2196f3' }
                            : {}
                        }
                      >
                        1º {topRankedUsers.firstplace!.name}
                      </h2>
                      <p>{topRankedUsers.firstplace!.school_name}</p>
                    </UserInfo>
                  </Avatar>
                  <Podium>
                    <Points>
                      {topRankedUsers.firstplace!.total_points} pts
                    </Points>
                    <FirstPodiumIcon width="15rem" />
                  </Podium>
                </PartyRanking>

                <PartyRanking>
                  <Avatar
                    $color="#B5B5B5"
                    $isLoggedUser={topRankedUsers.secondplace!.id === user.id}
                  >
                    {topRankedUsers.secondplace!.id === user.id && (
                      <WinnerBadge>2</WinnerBadge>
                    )}
                    <img
                      src={
                        topRankedUsers.secondplace!.avatar_url ||
                        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                      }
                      alt={`${topRankedUsers.secondplace!.name} Avatar`}
                    />
                    <UserInfo>
                      <h2
                        style={
                          topRankedUsers.secondplace!.id === user.id
                            ? { color: '#2196f3' }
                            : {}
                        }
                      >
                        2º {topRankedUsers.secondplace!.name}
                      </h2>
                      <p>{topRankedUsers.secondplace!.school_name}</p>
                    </UserInfo>
                  </Avatar>
                  <Podium>
                    <Points>
                      {topRankedUsers.secondplace!.total_points} pts
                    </Points>
                    <SecondPodiumIcon width="15rem" />
                  </Podium>
                </PartyRanking>
              </>
            )}
          </TopUsers>
          <RankingTableUsers>
            <RankingTable>
              <TableHeader>
                <tr>
                  <th style={{ width: '10%' }}>Posição</th>
                  <th style={{ width: '50%' }}>Estudante</th>
                  <th style={{ width: '20%' }}>Colégio</th>
                  <th style={{ width: '20%', textAlign: 'right' }}>Pontos</th>
                </tr>
              </TableHeader>
              <TableBody>
                {topRankedUsers.userInList && (
                  <tr
                    key={topRankedUsers.userInList.id}
                    style={{
                      background: '#2195f3d1',
                      color: '#fff',
                      fontWeight: 700,
                    }}
                  >
                    <td>
                      <Position $position={topRankedUsers.userInList.rank}>
                        {topRankedUsers.userInList.rank}
                      </Position>
                    </td>
                    <UserCell>
                      <img
                        src={
                          topRankedUsers.userInList.avatar_url ||
                          'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                        }
                        alt={`${topRankedUsers.userInList.name}'s avatar`}
                      />
                      <div>
                        <h3 style={{ color: '#fff' }}>
                          {topRankedUsers.userInList.name}
                        </h3>
                      </div>
                    </UserCell>
                    <td>{topRankedUsers.userInList.school_name}</td>
                    <PointsCell style={{ color: '#fff' }}>
                      {topRankedUsers.userInList.total_points} pts
                    </PointsCell>
                  </tr>
                )}
                {topRankedUsers.otherUsers.map(rankedUser => (
                  <tr key={rankedUser.id}>
                    <td>
                      <Position $position={rankedUser.rank}>
                        {rankedUser.rank}
                      </Position>
                    </td>
                    <UserCell>
                      <img
                        src={
                          rankedUser.avatar_url ||
                          'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/defaultUserImage.png'
                        }
                        alt={`${rankedUser.name}'s avatar`}
                      />
                      <div>
                        <h3>{rankedUser.name}</h3>
                      </div>
                    </UserCell>
                    <td>{rankedUser.school_name}</td>
                    <PointsCell>{rankedUser.total_points} pts</PointsCell>
                  </tr>
                ))}
              </TableBody>
            </RankingTable>
          </RankingTableUsers>
        </Dashboard>
      )}
    </Container>
  );
};

export default RankingPage;
